import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Archive, Ban, BellOff, Link2, LogOut, Search, Shield, UserMinus, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { api, errMsg } from "@/lib/api";
import { fdatetime } from "@/lib/format";
import { uploadFile } from "@/lib/upload";
import { Avatar } from "@/components/common";
import { MediaFile, useFileUrl } from "@/components/chat/Media";

export function ConvPhoto({ conv, size = 34 }) {
  const [url] = useFileUrl(conv.photo_file_id, !!conv.photo_file_id);
  if (url) return <img src={url} alt="" className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  return <Avatar name={conv.title} size={size} />;
}

function MediaTabs({ convId, onJump }) {
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("media");
  useEffect(() => {
    api.get(`/conversations/${convId}/media`).then(({ data }) => setD(data)).catch(() => {});
  }, [convId]);
  if (!d) return null;
  return (
    <div>
      <div className="flex gap-1 border-b border-mr-border mb-3">
        {[["media", "Médias"], ["documents", "Documents"], ["links", "Liens"]].map(([k, l]) => (
          <button key={k} data-testid={`info-tab-${k}`} onClick={() => setTab(k)} className={`px-3 h-10 text-sm border-b-2 -mb-px ${tab === k ? "border-mr-accent" : "border-transparent text-mr-muted"}`}>{l} ({d[k].length})</button>
        ))}
      </div>
      {tab === "media" && <div className="grid grid-cols-3 gap-1">{d.media.map((m) => <div key={m.id} className="overflow-hidden [&_img]:w-full [&_img]:h-24 [&_video]:w-full"><MediaFile id={m.media_file_ids[0]} kind={m.kind} /></div>)}</div>}
      {tab === "documents" && <div className="space-y-2">{d.documents.map((m) => <MediaFile key={m.id} id={m.media_file_ids[0]} kind="document" meta={m.meta} />)}</div>}
      {tab === "links" && <ul className="space-y-2 text-sm">{d.links.map((l, i) => <li key={i}><button className="text-left text-mr-accent break-all" onClick={() => onJump(l.id)}>{l.url}</button></li>)}</ul>}
    </div>
  );
}

function InConvSearch({ convId, onJump }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  useEffect(() => {
    if (q.trim().length < 2) {
      setRes([]);
      return undefined;
    }
    const t = setTimeout(() => api.get(`/conversations/${convId}/search`, { params: { q } }).then(({ data }) => setRes(data)).catch(() => {}), 300);
    return () => clearTimeout(t);
  }, [q, convId]);
  return (
    <div>
      <div className="relative"><Search size={15} className="absolute left-3 top-3.5 text-mr-muted" />
        <input data-testid="conv-search-input" className="mr-input pl-9" placeholder="Rechercher dans la conversation" value={q} onChange={(e) => setQ(e.target.value)} /></div>
      <ul className="mt-2 divide-y divide-mr-border">
        {res.map((m) => <li key={m.id}><button data-testid="conv-search-result" className="w-full text-left py-2 text-sm" onClick={() => onJump(m.id)}>
          <span className="font-medium">{m.sender_name}</span> · <span className="text-mr-muted text-xs">{fdatetime(m.created_at)}</span><br />{m.content}</button></li>)}
      </ul>
    </div>
  );
}

function AiSummary({ convId, onJump }) {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const go = (period) => {
    setBusy(true);
    api.post(`/ai/summary/${convId}`, { period }).then(({ data }) => setS(data)).catch((e) => toast.error(errMsg(e))).finally(() => setBusy(false));
  };
  const List = ({ title, items }) => items?.length > 0 && <div><p className="text-xs font-semibold mt-2">{title}</p>{items.map((i, k) => (
    <p key={k} className="text-sm">• {i.text} {i.message_ids.map((id, n) => <button key={id} className="text-mr-accent text-xs" onClick={() => onJump(id)}>[{n + 1}]</button>)}</p>))}</div>;
  return (
    <div className="mr-card p-3 space-y-2" data-testid="conv-ai-summary">
      <p className="text-sm font-medium">Résumé par l'assistant</p>
      <div className="flex gap-2"><button data-testid="summary-all" disabled={busy} className="mr-btn-secondary px-3 text-xs" onClick={() => go("all")}>Toute la conversation</button>
        <button data-testid="summary-week" disabled={busy} className="mr-btn-secondary px-3 text-xs" onClick={() => go("week")}>7 derniers jours</button></div>
      {busy && <p className="text-xs text-mr-muted">Résumé en cours…</p>}
      {s && <div data-testid="summary-result"><p className="text-sm">{s.summary}</p><List title="Décisions" items={s.decisions} /><List title="Questions ouvertes" items={s.open_questions} /></div>}
    </div>
  );
}

export function ConvInfo({ conv, open, onOpenChange, reload, onJump }) {
  const nav = useNavigate();
  const [title, setTitle] = useState(conv.title);
  const [desc, setDesc] = useState(conv.description || "");
  const [q, setQ] = useState("");
  const [found, setFound] = useState([]);
  const group = conv.type === "groupe";
  const editable = conv.is_admin && conv.type !== "direct";
  const call = async (fn, ok) => {
    try {
      await fn();
      if (ok) toast.success(ok);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  useEffect(() => {
    if (!q.trim()) {
      setFound([]);
      return;
    }
    api.get("/members", { params: { q } }).then(({ data }) => setFound(data.items.filter((m) => !conv.participant_ids.includes(m.id)))).catch(() => {});
  }, [q, conv.participant_ids]);
  const link = conv.invite_token ? `${window.location.origin}/app/messages/rejoindre/${conv.invite_token}` : "";
  const state = (k) => call(() => api.post(`/conversations/${conv.id}/state`, { [k]: !conv.state[k] }), "Préférence enregistrée");
  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto space-y-6 bg-mr-bg" data-testid="conv-info">
        <SheetTitle className="sr-only">Informations</SheetTitle>
        <div className="flex flex-col items-center gap-2 pt-4 text-center">
          <ConvPhoto conv={conv} size={80} />
          {editable && (
            <label className="text-xs text-mr-accent cursor-pointer">Changer la photo
              <input type="file" accept="image/*" hidden data-testid="conv-photo-input" onChange={(e) => e.target.files[0] && call(async () => {
                const id = await uploadFile("chat_photo", e.target.files[0]);
                await api.patch(`/conversations/${conv.id}`, { photo_file_id: id });
              }, "Photo mise à jour")} />
            </label>
          )}
          {editable ? (
            <div className="w-full space-y-2">
              <input data-testid="conv-title-input" className="mr-input" value={title} onChange={(e) => setTitle(e.target.value)} />
              <textarea data-testid="conv-description-input" className="mr-input min-h-[70px]" placeholder="Description du groupe" value={desc} onChange={(e) => setDesc(e.target.value)} />
              <button data-testid="conv-save-button" className="mr-btn-primary w-full" onClick={() => call(() => api.patch(`/conversations/${conv.id}`, { title, description: desc }), "Groupe mis à jour")}>Enregistrer</button>
            </div>
          ) : (
            <><p className="font-display font-bold text-xl">{conv.title}</p>{conv.description && <p className="text-sm text-mr-text2">{conv.description}</p>}</>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          <button data-testid="conv-mute-toggle" className="mr-btn-secondary px-3" onClick={() => state("muted")}><BellOff size={15} /> {conv.state.muted ? "Réactiver" : "Mettre en sourdine"}</button>
          <button data-testid="conv-archive-toggle" className="mr-btn-secondary px-3" onClick={() => state("archived")}><Archive size={15} /> {conv.state.archived ? "Désarchiver" : "Archiver"}</button>
          {conv.type === "direct" && (
            <button data-testid="conv-block-button" className="mr-btn-secondary px-3 text-mr-danger" onClick={() => call(() => (conv.blocked ? api.delete(`/members/${conv.other_id}/block`) : api.post(`/members/${conv.other_id}/block`)), conv.blocked ? "Membre débloqué" : "Membre bloqué")}>
              <Ban size={15} /> {conv.blocked ? "Débloquer" : "Bloquer"}</button>
          )}
        </div>
        <InConvSearch convId={conv.id} onJump={(id) => { onOpenChange(false); onJump(id); }} />
        <AiSummary convId={conv.id} onJump={(id) => { onOpenChange(false); onJump(id); }} />
        {group && conv.is_admin && link && (
          <div className="mr-card p-3 space-y-2" data-testid="conv-invite-link">
            <p className="text-sm font-medium flex items-center gap-2"><Link2 size={15} /> Lien d'invitation interne</p>
            <p className="text-xs text-mr-muted break-all">{link}</p>
            <div className="flex gap-2">
              <button data-testid="conv-copy-link" className="mr-btn-secondary px-3 text-xs" onClick={() => { navigator.clipboard?.writeText(link); toast.success("Lien copié"); }}>Copier</button>
              <button data-testid="conv-reset-link" className="mr-btn-ghost px-3 text-xs" onClick={() => call(() => api.post(`/conversations/${conv.id}/invite-link`), "Nouveau lien créé")}>Renouveler</button>
            </div>
          </div>
        )}
        {conv.type !== "annonce" && conv.type !== "direct" && (
          <div>
            <p className="mr-eyebrow mb-2">{conv.participants.length} participants</p>
            {group && conv.is_admin && (
              <div className="mb-3">
                <input data-testid="conv-add-member-input" className="mr-input" placeholder="Ajouter un membre…" value={q} onChange={(e) => setQ(e.target.value)} />
                {found.map((m) => <button key={m.id} data-testid="conv-add-member-option" className="w-full flex items-center gap-2 py-2 text-sm"
                  onClick={() => call(() => api.post(`/conversations/${conv.id}/members`, { member_ids: [m.id] }), `${m.name} ajouté`).then(() => setQ(""))}><UserPlus size={14} /> {m.name}</button>)}
              </div>
            )}
            <ul className="divide-y divide-mr-border">
              {conv.participants.map((p) => (
                <li key={p.id} className="flex items-center gap-2 py-2" data-testid="conv-participant">
                  <Avatar name={p.name} size={30} /><span className="flex-1 text-sm">{p.name}</span>
                  {p.admin && <span className="text-[10px] uppercase tracking-wider text-mr-accent">admin</span>}
                  {group && conv.is_admin && (
                    <>
                      <button aria-label="Administrateur" data-testid="conv-toggle-admin" onClick={() => call(() => api.post(`/conversations/${conv.id}/admins/${p.id}`))} className="w-8 h-8 grid place-items-center text-mr-muted hover:text-mr-text"><Shield size={14} /></button>
                      <button aria-label="Retirer" data-testid="conv-remove-member" onClick={() => call(() => api.delete(`/conversations/${conv.id}/members/${p.id}`), "Membre retiré")} className="w-8 h-8 grid place-items-center text-mr-muted hover:text-mr-danger"><UserMinus size={14} /></button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}
        <MediaTabs convId={conv.id} onJump={(id) => { onOpenChange(false); onJump(id); }} />
        {group && (
          <button data-testid="conv-leave-button" className="mr-btn-ghost w-full text-mr-danger" onClick={() => call(async () => {
            await api.delete(`/conversations/${conv.id}/members/${conv.me_id}`);
            nav("/app/messages");
          }, "Vous avez quitté le groupe")}><LogOut size={15} /> Quitter le groupe</button>
        )}
      </SheetContent>
    </Sheet>
  );
}
